import { ClientReportRow } from "@/data/models/ClientAdministrationRow";

/**
 * The admin portal's report list: the paging and display rules that are not the report's name
 * (which is IRES Office's own, `ReportLabel.ts`). Pure, so they are pinned by tests.
 */

/**
 * The rows after another page arrives. A report already listed is not listed twice: the cursor is a
 * report id, so a report added between two page loads can push one onto both pages.
 */
export function appendReportPage(existing: ClientReportRow[], page: ClientReportRow[]): ClientReportRow[] {
  const known = new Set(existing.map((row) => row.id));
  return [...existing, ...page.filter((row) => !known.has(row.id))];
}

/** The size in MB as IRES Admin shows it, or null when the report does not record one. */
export function reportFileSizeInMb(row: ClientReportRow): number | null {
  return row.fileSizeInBytes === null ? null : row.fileSizeInBytes / (1024 * 1024);
}

/**
 * Whose reports the header names in project mode: the project's name and number from its reports,
 * as no other source is fetched (the name stays out of the URL, it can be a customer's address).
 * Empty when the project has no reports.
 */
export function reportListProjectTitle(rows: ClientReportRow[]): string {
  const first = rows[0];

  if (!first) {
    return "";
  }

  return [first.projectName, first.projectNumber].filter((part): part is string => !!part).join(", ");
}

/** File types a browser shows in a tab, as IRES Cloud's report link treats them. */
const SHOWN_IN_TAB = ["pdf", "png", "jpg", "jpeg", "gif", "webp"];

/**
 * Whether a click opens the report in a new tab (a PDF or image) rather than downloading it in
 * place. IRES Cloud hands out every other file as a download, so a tab would stay blank.
 */
export function reportOpensInTab(row: ClientReportRow): boolean {
  return row.fileType !== null && SHOWN_IN_TAB.includes(row.fileType);
}
